import React from "react";
import CustomHeader from "../../components/header/index.jsx";
import {
  Palette, Puzzle, Cloud, Sparkles, ShieldCheck, ArrowRightLeft,
  CircleArrowUp, Briefcase, Plug, BarChart2, Gauge
} from "lucide-react";
import njclogo from "../../assets/icons/white_logo_png.png";
import "./features.scss";

// Static product-overview content. Copy is sourced from the real app strings
// (Smart Hub modes/agents, Migration directions, Brownfield, Connected Apps, Admin).
const FEATURES = [
  {
    icon: Sparkles,
    title: "Smart Hub — New Integrations",
    tag: "Greenfield",
    summary: "Design and build a brand-new integration from scratch, then deploy it — end to end, guided by AI agents.",
    points: [
      "Design Agent — turn a prompt into PlantUML flows, RAML API specs and architecture diagrams.",
      "Integration Agent — generate runnable integration code (Mule flows / XML) from the design.",
      "Deployment Agent — package and deploy to CloudHub, Runtime Fabric or push to GitHub.",
      "Target technologies: MuleSoft, Spring Boot and .NET.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Smart Hub — Enhance Existing",
    tag: "Brownfield",
    summary: "Analyse an existing codebase against your standards, and add new features to it — from a GitHub repo or an uploaded ZIP.",
    points: [
      "Compliance, Standards & Business-Logic Testing — scan a repo against a Confluence space, an uploaded document, or Intelog's built-in standard to surface violations and one-click fixes.",
      "Add a Feature — generate changes across the whole repo, preview the diffs, then commit.",
      "Source your project from a connected GitHub repo or by uploading a ZIP; download the updated project back.",
    ],
  },
  {
    icon: ArrowRightLeft,
    title: "Smart Hub — Migration",
    tag: "Convert platforms",
    summary: "Convert integrations between platforms with a live agent, then review and ship the result.",
    points: [
      "MuleSoft → .NET 8 — stream a full .NET project, chat-review the output, and push it to GitHub or Azure DevOps.",
      "Jitterbit → MuleSoft — upload a Jitterbit export and generate a complete Mule project you can browse and download.",
      "Bring source from an uploaded archive or straight from Git.",
    ],
  },
  {
    icon: CircleArrowUp,
    title: "Upgrade Agent",
    tag: "Modernize",
    summary: "Bring existing Mule applications up to date.",
    points: [
      "Mule runtime upgrades — move applications to newer Mule versions.",
      "Policy upgrades — review and modernize API policies.",
    ],
  },
  {
    icon: Briefcase,
    title: "Project Center",
    tag: "Manage",
    summary: "View and manage every integration project you've generated, all in one place.",
    points: [
      "See design, integration and migration projects together.",
      "Open a project to review its details, or remove ones you no longer need.",
    ],
  },
  {
    icon: Plug,
    title: "Connected Apps",
    tag: "Integrations",
    summary: "Connect Intelog to your enterprise systems so the agents can read repos, deploy, and sync docs.",
    points: [
      "Source control & CI: GitHub, GitLab, Bitbucket, Azure DevOps, Jenkins.",
      "Cloud & runtime: AWS, Azure, Google Kubernetes Engine (GKE), MuleSoft Anypoint.",
      "Docs: Confluence for standards and generated documentation.",
    ],
  },
  {
    icon: BarChart2,
    title: "Administration",
    tag: "Admins only",
    summary: "Manage who can use Intelog and what they can do. (Visible to administrators.)",
    points: [
      "Tenant Admin — invite users, assign roles & permissions, manage seat limits and monitor token usage.",
      "Super Admin — create tenants, choose subscription plans, and send onboarding invitations.",
    ],
  },
  {
    icon: Gauge,
    title: "Token Usage & Plans",
    tag: "Usage",
    summary: "Every AI generation is metered against your subscription plan.",
    points: [
      "Per-user or shared company token limits, based on your plan.",
      "Live usage indicators while you work, with the ability to request extensions.",
    ],
  },
];

// Small legend of the three agents that power Smart Hub (from the app's AGENTS list).
const AGENTS = [
  { icon: Palette, label: "Design Agent", sub: "PlantUML · RAML · Diagrams" },
  { icon: Puzzle, label: "Integration Agent", sub: "Flows · XML · Deploy" },
  { icon: Cloud, label: "Deployment Agent", sub: "CloudHub · RTF · GitHub" },
];

function Features() {
  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="features-page">
          {/* Hero */}
          <div className="features-hero">
            <img className="features-logo" src={njclogo} alt="INTELOG" />
            <div className="features-badge">Product Overview</div>
            <h1 className="features-title">Everything Intelog can do</h1>
            <p className="features-sub">
              An AI platform for designing, building, migrating and modernizing enterprise
              integrations — with a guided agent for every step.
            </p>
          </div>

          {/* Agent legend */}
          <div className="features-agents">
            {AGENTS.map((a) => {
              const Icon = a.icon;
              return (
                <div className="features-agent" key={a.label}>
                  <span className="features-agent-icon"><Icon size={20} /></span>
                  <div>
                    <div className="features-agent-label">{a.label}</div>
                    <div className="features-agent-sub">{a.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature sections */}
          <div className="features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <section className="features-card" key={f.title}>
                  <div className="features-card-head">
                    <span className="features-card-icon"><Icon size={22} /></span>
                    <div className="features-card-heading">
                      <h2 className="features-card-title">{f.title}</h2>
                      {f.tag && <span className="features-card-tag">{f.tag}</span>}
                    </div>
                  </div>
                  <p className="features-card-summary">{f.summary}</p>
                  <ul className="features-card-points">
                    {f.points.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>

          <p className="features-footnote">
            Some features are shown only when your account has the matching permission —
            ask your administrator if you don't see one you need.
          </p>
        </div>
      </main>
    </div>
  );
}

export default Features;
